import type { User } from '@paintscout/api'
import { useSearchUsersQuery } from '@paintscout/api'
import { buildScopedUserQuery, useClientOptions, useUser } from '@paintscout/ui-business'
import type { DropdownSelectOption } from '@paintscout/ui-business'
import * as Sentry from '@sentry/react'
import { useEffect, useMemo } from 'react'

type StaffUser = Pick<User, 'email'> & { app_metadata?: { active?: boolean | null } | null }

/**
 * Every active user with an email, as "To" options for a staff (work order) send. AddressBookSelect
 * stores each option's label, so the label must be the email.
 */
export function getStaffRecipientOptions(users: (StaffUser | null | undefined)[]): DropdownSelectOption[] {
  // Keyed by lowercase email, so one address in two casings is listed once (first casing wins).
  const emails = new Map<string, string>()
  for (const user of users) {
    const email = user?.app_metadata?.active ? user.email : null
    if (email && !emails.has(email.toLowerCase())) emails.set(email.toLowerCase(), email)
  }
  return [...emails.values()].sort((a, b) => a.localeCompare(b)).map((email) => ({ value: email, label: email }))
}

/**
 * "To" options for the send dialogs. Only the work order goes to staff, so other views get
 * `undefined` (no extra options) and skip the query.
 *
 * This runs its own search with `includeCrew: true` instead of reading `useUsers()`: the main app's
 * UsersProvider leaves painters out, and the server only adds them back for core seats and user
 * managers, so a work order would otherwise miss the crew it is for (#24836).
 */
export function useStaffRecipientOptions(view?: 'quote' | 'work-order'): DropdownSelectOption[] | undefined {
  const isWorkOrder = view === 'work-order'
  const { isSuperadmin, isSupport } = useUser()
  const { clientId } = useClientOptions()

  const { data, error } = useSearchUsersQuery({
    variables: {
      query: buildScopedUserQuery({ isPrivileged: isSuperadmin || isSupport, clientId }),
      includeCrew: true,
      limit: 1000
    },
    skip: !isWorkOrder,
    fetchPolicy: 'cache-and-network'
  })

  // A failed search leaves only the typed-email path, so report it rather than fail silently.
  useEffect(() => {
    if (error) Sentry.captureException(error, { tags: { feature: 'work-order-recipients' } })
  }, [error])

  const rows = data?.searchUsers?.rows
  return useMemo(() => (isWorkOrder ? getStaffRecipientOptions(rows ?? []) : undefined), [isWorkOrder, rows])
}
